package app.alefly.analytics

import app.alefly.domain.model.ChallengeId
import app.alefly.domain.model.Difficulty
import app.alefly.domain.model.Platform
import app.alefly.domain.model.TenantId

interface PageEventFactory {
    fun tenantView(
        tenantId: TenantId,
        locale: String,
        platform: Platform,
        difficulty: Difficulty,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.TENANT_VIEW,
        context = AnalyticsContext(
            tenantId = tenantId,
            locale = locale,
            platform = platform,
            difficulty = difficulty,
        ),
    )

    fun challengeView(
        tenantId: TenantId,
        challengeId: ChallengeId,
        locale: String,
        platform: Platform,
        difficulty: Difficulty,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.CHALLENGE_VIEW,
        context = AnalyticsContext(
            tenantId = tenantId,
            challengeId = challengeId,
            locale = locale,
            platform = platform,
            difficulty = difficulty,
        ),
    )

    fun pageView(
        pageName: String,
        locale: String,
        platform: Platform,
        tenantId: TenantId? = null,
        attributes: Map<String, String> = emptyMap(),
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.PAGE_VIEW,
        context = AnalyticsContext(
            tenantId = tenantId,
            locale = locale,
            platform = platform,
        ),
        attributes = mapOf(
            "pageName" to pageName,
        ) + attributes,
    )

    fun authSignIn(
        method: String,
        locale: String,
        platform: Platform,
        tenantId: TenantId? = null,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.AUTH_SIGN_IN,
        context = AnalyticsContext(
            tenantId = tenantId,
            locale = locale,
            platform = platform,
        ),
        attributes = mapOf(
            "method" to method,
        ),
    )

    fun displayNameChanged(
        locale: String,
        platform: Platform,
        tenantId: TenantId? = null,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.DISPLAY_NAME_CHANGED,
        context = AnalyticsContext(
            tenantId = tenantId,
            locale = locale,
            platform = platform,
        ),
    )

    fun seoToLobby(
        tenantId: TenantId,
        sourceType: String,
        sourcePath: String,
        platform: Platform,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.SEO_TO_LOBBY,
        context = AnalyticsContext(
            tenantId = tenantId,
            locale = "pt",
            platform = platform,
        ),
        attributes = mapOf(
            "sourceType" to sourceType,
            "sourcePath" to sourcePath,
        ),
    )

    fun notificationTapped(
        tenantId: TenantId,
        locale: String,
        platform: Platform,
        notificationType: String,
        deepLinkUrl: String,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.NOTIFICATION_TAPPED,
        context = AnalyticsContext(
            tenantId = tenantId,
            locale = locale,
            platform = platform,
        ),
        attributes = mapOf(
            "notificationType" to notificationType,
            "deepLinkUrl" to deepLinkUrl,
        ),
    )

    fun adImpression(
        tenantId: TenantId,
        placement: String,
        platform: Platform,
        locale: String = "pt",
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.AD_IMPRESSION,
        context = AnalyticsContext(
            tenantId = tenantId,
            locale = locale,
            platform = platform,
        ),
        attributes = mapOf(
            "placement" to placement,
        ),
    )

    fun adBannerImpression(
        tenantId: TenantId,
        placement: String,
        game: String,
        platform: Platform,
        locale: String = "pt",
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.AD_BANNER_IMPRESSION,
        context = AnalyticsContext(tenantId = tenantId, locale = locale, platform = platform),
        attributes = mapOf("placement" to placement, "game" to game),
    )

    fun rewardedAdCompleted(
        tenantId: TenantId,
        challengeId: ChallengeId,
        difficulty: Difficulty,
        locale: String,
        platform: Platform,
        placement: String,
        status: String = "completed",
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.REWARDED_AD_COMPLETED,
        context = AnalyticsContext(
            tenantId = tenantId,
            challengeId = challengeId,
            difficulty = difficulty,
            locale = locale,
            platform = platform,
        ),
        attributes = mapOf(
            "placement" to placement,
            "status" to status,
        ),
    )

    fun relatedTenantsImpression(
        tenantId: TenantId,
        locale: String,
        platform: Platform,
        relatedIds: List<String>,
        placement: String = "result_bottom",
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.RELATED_TENANTS_IMPRESSION,
        context = AnalyticsContext(
            tenantId = tenantId,
            locale = locale,
            platform = platform,
        ),
        attributes = mapOf(
            "related_ids" to relatedIds.joinToString(","),
            "count" to relatedIds.size.toString(),
            "placement" to placement,
        ),
    )

    fun relatedTenantClick(
        tenantId: TenantId,
        targetTenantId: String,
        position: Int,
        locale: String,
        platform: Platform,
        placement: String = "result_bottom",
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.RELATED_TENANT_CLICK,
        context = AnalyticsContext(
            tenantId = tenantId,
            locale = locale,
            platform = platform,
        ),
        attributes = mapOf(
            "target_tenant_id" to targetTenantId,
            "position" to position.toString(),
            "placement" to placement,
        ),
    )
}
